import React, { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { useFocusElement, useLanguage } from 'hooks';
import useStores from 'hooks/use-store';
import Spinner from 'components/shared/Spinner';
import {
  getStoredAuctionIdByPath,
  getStoredAuctionLaneIdByPath,
  clearLocalStorageItems,
} from 'shared/utils/localStorage';
import SoftLoadingOverlay from 'components/shared/SoftLoadingOverlay';
import { Icon } from 'components/project/ClerkScreen';
import {
  Container,
  HeadingContainer,
  LogoContainer,
  Title,
  FormWrapper,
  BlockFormField,
  LoginButton,
  LoginLabel,
  LoginForm,
  Error,
  ChangeAuctionButton,
  ChangeAuctionButtonContainer,
  ResetButton,
  WatchModeContainer,
} from './Styles';
import LayoutSettings from './LayoutSettings';
import { isEnterKey } from '../../components/project/ClerkScreen/shared/utils/common';
import { clearStorage } from '../../shared/utils/localStorage';
import { version } from '../../../package.json';
import Version from '../../components/shared/Version/index';

const Login = () => {
  const {
    AuctionStore: {
      auctionID,
      auctionLaneId,
      auctionList,
      connected,
      laneList,
      setProperty,
    },
    AuthStore: { errorMessage, setProperty: setAuthProperty },
    UIStore: {
      handleEndLane,
      handleInitializeScreenAuctions,
      handleInitializeScreenLanes,
      handleJoinAuction,
      handleJoinLane,
      handleLogin,
      handleLogout,
    },
  } = useStores();

  const {
    selectOneValueText,
    headerText,
    loginButtonText,
    userNameLabel,
    passwordLabel,
    auctionLabelText,
    laneLabelText,
    logoutButtonText,
  } = useLanguage();
  const RESETTING_TEXT = `Resetting Cache...`;
  const RESET_TEXT = `Issues? Reset the cache`;

  const history = useHistory();
  const [username, setUsername] = useState(window.sessionStorage.getItem('userName') || '');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const watchModeRef = useRef();
  const loginButtonRef = useRef();
  const [userNameRef] = useFocusElement(username === '');
  const [auctionRef] = useFocusElement(getStoredAuctionIdByPath() || true);
  const [resetMessage, setResetMessage] = useState(RESET_TEXT);

  const isResetting = resetMessage === RESETTING_TEXT;

  setProperty('onClerkScreen', false);

  /** If we're connected, and we've selected an auction ID, go to the main screen */
  // if (connected && auctionLaneId) {
  //   if (props.location && props.location.state) {
  //     history.replace(props.location.state.from.pathname);
  //   }
  // }
  const loginError = window.sessionStorage.getItem('loginError');

  const handleWatchModeChange = (value) =>
    window.localStorage.setItem('monitorChatOnly', value);

  const resetLoginStorage = (e) => {
    e.preventDefault();
    setResetMessage(RESETTING_TEXT);
    watchModeRef.current.checked = false;
    handleWatchModeChange(false);

    clearStorage(true);
    window.location.reload(true);
    setTimeout(() => {
      setResetMessage(RESET_TEXT);
    }, 1000);
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    setIsLoading(true);
    handleLogin(username, password);
  };
  const handlePasswordOnKeyPress = (e) => {
    if (isEnterKey(e)) {
      setIsLoading(true);
      handleLogin(username, password);
    }
  };

  const handleJoinAuctionDuringLogin = (auctionId) => {
    handleJoinAuction(auctionId);
    handleInitializeScreenAuctions(auctionId);
  };

  const handleJoinLaneDuringLogin = (laneId) => {
    handleJoinLane(laneId);
    handleInitializeScreenLanes(laneId);
  };

  const clearErrorMessage = () => {
    setAuthProperty('errorMessage', '');
  };

  useEffect(() => {
    if (!auctionLaneId) {
      setProperty('connected', false);
      clearLocalStorageItems();
    }
  }, [auctionLaneId]);

  useEffect(() => {
    const versionNode = document.getElementsByClassName('version')[0];
    if (versionNode) {
      versionNode.innerHTML = `v${version}`;
      versionNode.style.color = '#000';
      versionNode.style.position = 'absolute';
      versionNode.style.marginTop = '10px';
      versionNode.style.textAlign = 'right';
      versionNode.style.marginLeft = '-10px';
    }

    setIsLoading(false);
  }, []);

  // TODO: Add a spinner to the Login button. Let user know something is happening...
  return (
    <>
      <Helmet>
        <title>
          {connected && auctionID && auctionLaneId
            ? 'Simulcast - Launchpad'
            : 'Simulcast - Clerk Screen'}
        </title>
      </Helmet>
      <Version />
      <Container>
        <SoftLoadingOverlay />
        <HeadingContainer>
          <LogoContainer>
            <img
              src={`${process.env.PUBLIC_URL}/assets/logo-inverse.png`}
              alt="National Powersport Auctions"
            />
          </LogoContainer>
          <Title>
            <>
              {connected && auctionID && auctionLaneId
                ? 'Simulcast Launchpad'
                : headerText}
              {/* <span className="version" /> */}
            </>
          </Title>
        </HeadingContainer>
        <FormWrapper>
          <LoginForm>
            {!connected && (
              <>
                <BlockFormField>
                  <LoginLabel>{userNameLabel}</LoginLabel>
                  <input
                    data-testid="username"
                    ref={userNameRef}
                    type="text"
                    autoComplete="off"
                    tabIndex={1}
                    value={username}
                    onChange={(event) => {
                      clearErrorMessage();
                      setUsername(event.target.value);
                    }}
                  />
                </BlockFormField>
                <BlockFormField>
                  <LoginLabel>{passwordLabel}</LoginLabel>
                  <input
                    data-testid="password"
                    type="password"
                    autoComplete="off"
                    tabIndex={2}
                    value={password}
                    onKeyPress={handlePasswordOnKeyPress}
                    onChange={(event) => {
                      clearErrorMessage();
                      setPassword(event.target.value);
                    }}
                  />
                </BlockFormField>
                {loginError && <Error>{loginError}</Error>}
                <WatchModeContainer>
                  <input
                    ref={watchModeRef}
                    type="checkbox"
                    onChange={(e) => handleWatchModeChange(e.target.checked)}
                  />
                  <LoginLabel>
                    Check box if you are logging in to watch/monitor the auction
                    only
                  </LoginLabel>
                </WatchModeContainer>
                <BlockFormField>
                  <LoginButton
                    data-testid="login"
                    ref={loginButtonRef}
                    disabled={username === '' || password === ''}
                    tabIndex={3}
                    type="button"
                    onClick={() => {
                      window.sessionStorage.setItem('userName', username);
                      handleLogin(username, password);
                    }}
                  >
                    <Icon icon="user" />
                    {loginButtonText}
                  </LoginButton>
                  <ResetButton
                    bgColor={isResetting ? '#21BA45' : null}
                    type="button"
                    onClick={resetLoginStorage}
                  >
                    {!isResetting && <Icon icon="refresh" />}
                    {resetMessage}
                  </ResetButton>
                </BlockFormField>
              </>
            )}
            {connected && !auctionLaneId && (
              <>
                {auctionList && auctionList.length > 0 ? (
                  <BlockFormField>
                    {console.log('UI BLOCK - load auction list into combo', {
                      combo: auctionRef.current,
                      // connected,
                      // auctionLaneId,
                      // auctionList: JSON.stringify(toJS(auctionList)),
                      // auctionIdInStorage: getStoredAuctionIdByPath(),
                    })}
                    <LoginLabel>{auctionLabelText}</LoginLabel>
                    <select
                      data-testid="auction-list"
                      ref={auctionRef}
                      value={getStoredAuctionIdByPath() || ''}
                      onChange={(e) =>
                        handleJoinAuctionDuringLogin(e.target.value)
                      }
                    >
                      <option value="">{selectOneValueText}</option>
                      {auctionList.map((item) => (
                        <option key={item.auctionID} value={item.auctionID}>
                          {item.auction}
                        </option>
                      ))}
                    </select>
                  </BlockFormField>
                ) : !isLoading ? (
                  <div>
                    <Icon icon="exclamation-triangle" size={1} iconOnly />
                    Oops! It appears that currently, there are no auctions to
                    display! Please contact technical support if you feel this
                    is an error.
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Spinner />
                  </div>
                )}

                {laneList && laneList.length > 0 && (
                  <BlockFormField>
                    <LoginLabel>{laneLabelText}</LoginLabel>
                    <select
                      data-testid="lane-list"
                      value={getStoredAuctionLaneIdByPath() || ''}
                      onChange={(e) =>
                        handleJoinLaneDuringLogin(e.target.value)
                      }
                    >
                      <option value="">{selectOneValueText}</option>
                      {laneList.map((item) => (
                        <option key={item.laneID} value={item.laneID}>
                          {item.lane}
                        </option>
                      ))}
                    </select>
                  </BlockFormField>
                )}
              </>
            )}

            {connected && auctionID && auctionLaneId && (
              <>
                <LayoutSettings />
                <ChangeAuctionButtonContainer>
                  {/* <ChangeAuctionButton
                    data-testid="change-auction-lane"
                    onClick={() => {
                      handleEndLane();
                      removeStoredAuctionIdByPath();
                      removeStoredAuctionLaneIdByPath();
                    }}
                  >
                    {switchAuctionButtonText}
                  </ChangeAuctionButton> */}
                  <ChangeAuctionButton
                    data-testid="logout"
                    onClick={() => handleLogout()}
                  >
                    {logoutButtonText}
                  </ChangeAuctionButton>
                </ChangeAuctionButtonContainer>
              </>
            )}
            {errorMessage && loginButtonRef.current && (
              <Error>{errorMessage}</Error>
            )}
          </LoginForm>
        </FormWrapper>
      </Container>
    </>
  );
};

export default observer(Login);
